import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { getArticle, CATEGORIES, articlesByCategory } from "./helpData";
import { ArrowLeft, ArrowRight, Check, Lightbulb, Clock } from "lucide-react";

const HelpHeader = () => (
  <header className="border-b border-border sticky top-0 z-30 bg-background/90 backdrop-blur">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2.5">
        <img src="/azordi-logo.webp" alt="azordi" className="h-7 w-auto" />
      </Link>
      <nav className="flex items-center gap-5 text-sm">
        <Link to="/help" className="text-muted-foreground hover:text-foreground" data-testid="article-back-help">Help Centre</Link>
        <Link to="/login" className="text-muted-foreground hover:text-foreground">Sign in</Link>
      </nav>
    </div>
  </header>
);

export default function HelpArticle() {
  const { slug } = useParams();
  const a = getArticle(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!a) return <Navigate to="/help" replace />;

  const cat = CATEGORIES.find((c) => c.slug === a.category);
  const related = articlesByCategory(a.category).filter((x) => x.slug !== a.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="help-article-page">
      <HelpHeader />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/help" className="hover:text-foreground">Help</Link>
          <span>/</span>
          <span>{cat?.title}</span>
        </div>

        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight" data-testid="help-article-title">{a.title}</h1>
        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-3">
          <Clock className="h-4 w-4" /> {a.readMins} min read
        </div>
        <p className="text-base sm:text-lg text-muted-foreground mt-5 leading-relaxed">{a.intro}</p>

        {a.screenshot && (
          <figure className="mt-8 rounded-2xl overflow-hidden border border-border shadow-sm">
            <img src={a.screenshot} alt={`${a.title} screenshot`} className="w-full block" data-testid="help-article-screenshot" loading="lazy" />
          </figure>
        )}

        <section className="mt-10">
          <h2 className="font-heading font-bold text-xl mb-5">Step by step</h2>
          <ol className="space-y-4">
            {a.steps.map((s, i) => (
              <li key={i} className="flex gap-4" data-testid={`help-step-${i}`}>
                <span className="shrink-0 h-7 w-7 rounded-full bg-primary text-primary-foreground text-sm font-semibold flex items-center justify-center">{i + 1}</span>
                <span className="text-base leading-relaxed pt-0.5">{s}</span>
              </li>
            ))}
          </ol>
        </section>

        {a.shots?.length > 0 && (
          <section className="mt-10 space-y-6" data-testid="help-article-shots">
            {a.shots.map((s, i) => (
              <figure key={i} className="rounded-2xl overflow-hidden border border-border shadow-sm">
                <img src={s.src} alt={s.caption || `${a.title} ${i + 1}`} className="w-full block" loading="lazy" data-testid={`help-shot-${i}`} />
                {s.caption && <figcaption className="text-xs text-muted-foreground px-4 py-2.5 bg-muted/40 border-t border-border">{s.caption}</figcaption>}
              </figure>
            ))}
          </section>
        )}

        {a.tips?.length > 0 && (
          <section className="mt-10 rounded-2xl border border-border bg-muted/40 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold mb-3"><Lightbulb className="h-4 w-4 text-primary" /> Tips</div>
            <ul className="space-y-2.5">
              {a.tips.map((t, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" /> <span>{t}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {related.length > 0 && (
          <section className="mt-14 border-t border-border pt-8">
            <h2 className="font-heading font-bold text-lg mb-4">Related guides</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/help/${r.slug}`} className="group flex items-center justify-between p-4 rounded-xl border border-border hover:border-primary/50 transition-colors" data-testid={`related-${r.slug}`}>
                  <span className="text-sm font-medium group-hover:text-primary transition-colors">{r.title}</span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12">
          <Link to="/help" className="inline-flex items-center gap-2 text-sm text-primary hover:underline" data-testid="help-article-back">
            <ArrowLeft className="h-4 w-4" /> Back to Help Centre
          </Link>
        </div>
      </main>
    </div>
  );
}
